/**
 * Shared identity types — the Atlas Passport contract.
 *
 * These mirror the server `account` table (server/src/schema/identity.ts).
 * Columns are only ever ADDED, never renamed (see CLAUDE.md).
 */

/** A SpacetimeDB caller identity rendered as a hex string on the client. */
export type IdentityHex = string;

/** Mirror of the server `account` row. */
export interface Account {
  /** Hex form of the SpacetimeDB Identity (primary key). */
  identity: IdentityHex;
  /** Auto-increment numeric id (gaps are normal; never use for ordering). */
  accountId: bigint;
  /** Permanent login handle (unique). */
  username: string;
  /** Display name (changeable weekly — enforcement lands later). */
  displayName: string;
  /** Microseconds since unix epoch. */
  createdAt: bigint;
  /** Microseconds since unix epoch. */
  lastSeen: bigint;
}

/** Arguments to the `createAccount` reducer. */
export interface CreateAccountArgs {
  username: string;
  displayName: string;
}

/**
 * Mirror of the server `display_name_change` row — the weekly-cooldown ledger.
 * Absence of a row means the display name has never been changed (the signup
 * name is not a "change"), so the first `changeDisplayName` is always allowed.
 */
export interface DisplayNameChange {
  identity: IdentityHex;
  /** Microseconds since unix epoch of the last successful change. */
  lastChangedAt: bigint;
}

/** Arguments to the `changeDisplayName` reducer. */
export interface ChangeDisplayNameArgs {
  newName: string;
}

/** Display-name change cooldown: one week, in microseconds. */
export const DISPLAY_NAME_COOLDOWN_MICROS = 604_800_000_000n;

// Re-export the avatar contract so identity consumers get the whole Passport.
export type {
  Avatar,
  AvatarAppearance,
  BodyTemplate,
  MaterialSlot,
  MaterialSlots,
  Attachments,
  SetAvatarArgs,
} from '../avatar.js';
